import React from "react";
import { RootState, ThunkAppDispatch } from "redux/store";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import * as Mui from "@mui/material";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import * as Router from "react-router-dom";
import { DashboardLayout } from "component/layout/dashboard";
import { PageProps } from "shared/page-props";
import * as Icons from "react-feather";
import { FeatherIcon } from "component/shared/feather-icon";
import { getGroupedDripCampaigns, getCampaignRoutes } from "redux/selector";
import { CampaignCategoryGroup } from "shared/campaign-grouping";
import { styles } from "./style";
import { AddCampaign } from "./add-campaign";
import { CategoryAccordion } from "component/shared/category-accordion";
import { AddCategoryDialog } from "./category-dialogs/add-category-dialog";
import { RenameCategoryDialog } from "./category-dialogs/rename-category-dialog";
import { DeleteCategoryDialog } from "./category-dialogs/delete-category-dialog";
import { MoveCampaignDialog } from "./category-dialogs/move-campaign-dialog";
import { UnauthorizedMa } from "./unauthorized";
import { createCampaign, updateCampaign } from "redux/slice/campaigns";
import { createCampaignMessage } from "redux/slice/campaign-messages";
import { createCampaignCampaignMessage } from "redux/slice/campaign-campaign-messages";
import { updateCampaignRoute } from "redux/slice/campaign-routes";
import { Campaign } from "model/campaign";
import { CampaignStatus } from "type/campaign-status";
import { isCampaignActivationBlocked, duplicateStatus } from "shared/campaign-activation";
import { ActivationDialog, ActivationDialogVariant } from "./activation-dialog";
import { Dialog } from "component/shared/dialog";
import { RouterLinkWrapper } from "component/shared/router-link-wrapper";
import { urls } from "routes/urls";
import * as paths from "routes/paths";
import Paper from "@mui/material/Paper";
import { WarningOutlined } from "@mui/icons-material";
import CopyToClipboard from "react-copy-to-clipboard";
import MuiAlert from "@mui/material/Alert";

const mapStateToProps = (state: RootState) => ({
	grouped: getGroupedDripCampaigns(state),
	campaignRoutes: getCampaignRoutes(state),
});

/*
 * ThunkAppDispatch is used here instead of the plain Dispatch + getPayload() pattern
 * found in older components in this codebase. Exposing typed dispatch directly enables
 * the modern RTK .unwrap() idiom in handleDuplicate(), which throws on rejection and
 * returns the correct payload type without casting. This is the RTK-recommended approach;
 * the legacy getPayload() pattern is a local workaround for a TypeScript inference gap
 * in bindActionCreators and is considered technical debt. -DW
 */
const mapDispatchToProps = (dispatch: ThunkAppDispatch) => ({
	...bindActionCreators({ updateCampaign, updateCampaignRoute }, dispatch),
	dispatch,
});

interface Props extends
	ReturnType<typeof mapStateToProps>,
	ReturnType<typeof mapDispatchToProps>,
	PageProps,
	Router.RouteComponentProps,
	WithStyles<typeof styles> {
}

interface State {
	openAddNew: boolean;
	// Expand state is local, keyed by group id (category id, or the Uncategorized sentinel).
	// Default: all collapsed (empty map). No persistence — resets on navigation.
	expanded: { [groupId: number]: boolean };
	campaignToDelete?: Campaign;
	campaignToActivate?: Campaign;
	// MAX-4201: which activation dialog to show. Set when the dialog opens and left alone when it
	// closes, so the content doesn't flip variants during the close transition.
	activationVariant: ActivationDialogVariant;
	// MAX-3878 category management (all local UI state).
	openAddCategory: boolean;
	categoryMenuAnchor?: HTMLElement;
	categoryMenuGroup?: CampaignCategoryGroup;
	renameGroup?: CampaignCategoryGroup;
	deleteGroup?: CampaignCategoryGroup;
	rowMenuAnchor?: HTMLElement;
	rowMenuCampaign?: Campaign;
	campaignToMove?: Campaign;
	// MAX-3871 — "Copied" confirmation toast for the ID column's copy affordance.
	showCopiedToast: boolean;
}


class Component extends React.Component<Props, State> {
	public constructor(props: Props) {
		super(props);
		this.state = {
			openAddNew: false,
			expanded: {},
			activationVariant: "confirm",
			openAddCategory: false,
			showCopiedToast: false,
		};
	}

	private handleToggleExpand(groupId: number) {
		this.setState((prev) => ({
			expanded: { ...prev.expanded, [groupId]: !prev.expanded[groupId] },
		}));
	}

	private handleToggleActivate(campaign: Campaign) {
		if (campaign.status === CampaignStatus.ACTIVE) {
			this.props.updateCampaign({ campaign: { ...campaign, status: CampaignStatus.INACTIVE } });
		} else {
			// MAX-4201: a Market campaign with no Market gets the blocking dialog instead of the
			// confirmation. Deactivating (above) is never intercepted.
			this.setState({
				campaignToActivate: campaign,
				activationVariant: isCampaignActivationBlocked(campaign) ? "blocked" : "confirm",
			});
		}
	}

	private handleConfirmActivate() {
		const { campaignToActivate } = this.state;
		// MAX-4201: a blocked campaign never reaches here (its dialog offers Go to Settings instead),
		// but guard anyway so a stale click can't send a request the server will reject.
		if (campaignToActivate && !isCampaignActivationBlocked(campaignToActivate)) {
			this.props.updateCampaign({ campaign: { ...campaignToActivate, status: CampaignStatus.ACTIVE } });
		}
		this.setState({ campaignToActivate: undefined });
	}

	// MAX-4201: from the blocking dialog, open the campaign's Settings scrolled to the Market field.
	private handleGoToSettings() {
		const { campaignToActivate } = this.state;
		this.setState({ campaignToActivate: undefined });
		if (campaignToActivate) {
			this.props.history.push(urls.campaign(campaignToActivate, { tab: "settings", focus: "market" }));
		}
	}

	private handleDeleteConfirm(campaign: Campaign) {
		// MAX-3857: deactivate rather than delete -- matches the "Campaign in use" dialog copy below,
		// which already promised deactivation.
		const routesForCampaign = this.props.campaignRoutes.filter((r) => r.campaign?.id === campaign.id);
		routesForCampaign.forEach((route) => this.props.updateCampaignRoute({ campaignRoute: { ...route, inactive: true } }));
		this.props.updateCampaign({ campaign: { ...campaign, inactiveYn: true } });
		this.setState({ campaignToDelete: undefined });
	}

	private async handleDuplicate(campaign: Campaign) {
		const { dispatch } = this.props;
		const { id: _id, dateCreated: _dc, subscriptions: _sub, campaignCampaignMessages, campaignStat: _stat, ...campaignFields } = campaign;
		// MAX-4201: a copy of a blocked campaign is created Inactive — the server rejects an active
		// Market campaign with no Market.
		const newCampaign = await dispatch(createCampaign({
			campaign: { ...campaignFields, name: `${campaign.name} (Copy)`, status: duplicateStatus(campaign) },
		})).unwrap();
		for (const ccm of campaignCampaignMessages) {
			const { id: _mid, dateCreated: _mdc, ...messageFields } = ccm.campaignMessage;
			const newMsg = await dispatch(createCampaignMessage({ campaignMessage: messageFields })).unwrap();
			await dispatch(createCampaignCampaignMessage({
				campaignCampaignMessage: {
					campaignId: newCampaign.id,
					campaignMessageId: newMsg.id,
					messageOrder: ccm.messageOrder,
				},
			})).unwrap();
		}
	}

	// The expanded body of each accordion: the current campaigns table, scoped to one group.
	// Columns: Campaign / ID / Sent / Subscribers / Opens / Links / Active + row actions —
	// the Links→Clicks rename is out of scope (separate story, MAX-3878).
	private renderCampaignsTable(campaigns: Campaign[]) {
		const { classes = {} } = this.props;
		return (
			<Mui.TableContainer component={Paper} style={{ boxShadow: 'none', borderRadius: 0 }}>
				<Mui.Table aria-label="Campaigns">
					<Mui.TableHead>
						<Mui.TableRow>
							<Mui.TableCell className={classes.tableHeaderCell}>Campaign</Mui.TableCell>
							<Mui.TableCell className={classes.tableHeaderCell} align="center">ID</Mui.TableCell>
							<Mui.TableCell className={classes.tableHeaderCell} align="center">Sent</Mui.TableCell>
							<Mui.TableCell className={classes.tableHeaderCell} align="center">Subscribers</Mui.TableCell>
							<Mui.TableCell className={classes.tableHeaderCell} align="center">Opens</Mui.TableCell>
							<Mui.TableCell className={classes.tableHeaderCell} align="center">Links</Mui.TableCell>
							<Mui.TableCell className={classes.tableHeaderCell} align="center">Active</Mui.TableCell>
							<Mui.TableCell className={classes.tableHeaderCell} style={{ width: 90 }} />
						</Mui.TableRow>
					</Mui.TableHead>
					<Mui.TableBody>
						{campaigns.length === 0 && (
							<Mui.TableRow>
								<Mui.TableCell colSpan={8} className={classes.emptyCategory}>
									<Mui.Typography variant="body2" color="textSecondary" align="center">
										No campaigns in this category.
									</Mui.Typography>
								</Mui.TableCell>
							</Mui.TableRow>
						)}
						{campaigns.map((campaign) => (
							<Mui.TableRow key={campaign.id}>
								<Mui.TableCell>
									<RouterLinkWrapper
										className={classes.link}
										style={{ fontWeight: 500 }}
										to={urls.campaign(campaign)}
									>
										{campaign.name}
									</RouterLinkWrapper>
									{/* MAX-4201: flag an inactive campaign that can't be activated until a Market is chosen. */}
									{isCampaignActivationBlocked(campaign) && campaign.status !== CampaignStatus.ACTIVE && (
										<div className={classes.marketNeeded}>
											<WarningOutlined className={classes.marketNeededIcon} />
											<Mui.Typography variant="caption">Market Needed</Mui.Typography>
										</div>
									)}
								</Mui.TableCell>
								<Mui.TableCell align="center">
									<div className={classes.idCell}>
										<Mui.Typography variant="body2">{campaign.id}</Mui.Typography>
										<CopyToClipboard
											text={`${campaign.id}`}
											onCopy={() => this.setState({ showCopiedToast: true })}
										>
											<Mui.IconButton
												size="small"
												aria-label="Copy campaign ID"
												className={classes.copyIconButton}
											>
												<FeatherIcon><Icons.Copy size={18} /></FeatherIcon>
											</Mui.IconButton>
										</CopyToClipboard>
									</div>
								</Mui.TableCell>
								<Mui.TableCell align="center">{campaign.campaignStat?.sents ?? 0}</Mui.TableCell>
								<Mui.TableCell align="center">{campaign.subscriptions?.length ?? 0}</Mui.TableCell>
								<Mui.TableCell align="center">{campaign.campaignStat?.opens ?? 0}</Mui.TableCell>
								<Mui.TableCell align="center">{campaign.campaignStat?.clicks ?? 0}</Mui.TableCell>
								<Mui.TableCell align="center">
									<Mui.Switch
										checked={campaign.status === CampaignStatus.ACTIVE}
										onChange={() => this.handleToggleActivate(campaign)}
										color="secondary"
										size="small"
									/>
								</Mui.TableCell>
								<Mui.TableCell align="right" style={{ whiteSpace: 'nowrap' }}>
									{/* Row actions in the kebab (per design): Move to Category / Duplicate / Delete. Edit is via the title link. */}
									<Mui.IconButton
										size="small"
										className={classes.kebab}
										onClick={(e) => this.setState({ rowMenuAnchor: e.currentTarget, rowMenuCampaign: campaign })}
									>
										<FeatherIcon><Icons.MoreVertical /></FeatherIcon>
									</Mui.IconButton>
								</Mui.TableCell>
							</Mui.TableRow>
						))}
					</Mui.TableBody>
				</Mui.Table>
			</Mui.TableContainer>
		);
	}

	private renderCategoryAccordion(group: CampaignCategoryGroup) {
		const { classes = {} } = this.props;
		const expanded = !!this.state.expanded[group.id];
		return (
			<CategoryAccordion
				key={group.id}
				expanded={expanded}
				onToggle={() => this.handleToggleExpand(group.id)}
				disableDetailsPadding
				summary={
					<div className={classes.accordionHeader}>
						{/* Chevron pinned far left: down-caret at rest, flips up when expanded (figma 989:13617) */}
						<FeatherIcon className={`${classes.chevron} ${expanded ? classes.chevronOpen : ''}`}>
							<Icons.ChevronDown />
						</FeatherIcon>
						<Mui.Typography variant="h5" className={classes.categoryName}>
							{group.name}
						</Mui.Typography>
						<div className={classes.headerSpacer} />
						<Mui.Typography variant="body1" color="textSecondary" className={classes.categoryCount}>
							{group.count} Campaigns
						</Mui.Typography>
						{/* Category kebab — Rename / Delete. Hidden for the virtual Uncategorized bucket
							(null-FK sink, inherently non-editable / non-deletable per AR7). */}
						{!group.uncategorized && (
							<Mui.IconButton
								size="small"
								className={classes.kebab}
								onClick={(e) => {
									e.stopPropagation();
									this.setState({ categoryMenuAnchor: e.currentTarget, categoryMenuGroup: group });
								}}
							>
								<FeatherIcon><Icons.MoreVertical /></FeatherIcon>
							</Mui.IconButton>
						)}
					</div>
				}
			>
				{this.renderCampaignsTable(group.campaigns)}
			</CategoryAccordion>
		);
	}

	public render() {
		const { user, grouped, campaignRoutes, classes = {} } = this.props;
		const {
			openAddNew, campaignToDelete, campaignToActivate,
			openAddCategory, categoryMenuAnchor, categoryMenuGroup,
			renameGroup, deleteGroup, rowMenuAnchor, rowMenuCampaign, campaignToMove,
		} = this.state;
		const title = "Campaigns";

		const inUseCount = campaignToDelete
			? campaignRoutes.filter((r) => r.campaign?.id === campaignToDelete.id).length
			: 0;
		const isInUse = inUseCount > 0;

		// Last-category guard (client side): the last remaining REAL category can't be deleted
		// (Uncategorized is virtual and not counted). Server also enforces this with a 409.
		const realCategoryCount = grouped.groups.filter((g) => !g.uncategorized).length;
		const isLastRealCategory = realCategoryCount <= 1;

		// Source category name for the Move-to-Category body copy ("Move {name} from {source} to:").
		const moveSourceName = campaignToMove
			? grouped.groups.find((g) => g.campaigns.some((c) => c.id === campaignToMove.id))?.name
			: undefined;

		return (
			<DashboardLayout
				permitted={user && user.permissions.campaigns}
				title={title}
				header={
					<Mui.Typography variant="h1">
						<FeatherIcon>
							<Icons.Send />
						</FeatherIcon>
						{title}
					</Mui.Typography>
				}
			>
				{user.pending ? (
					<UnauthorizedMa />
				) : (
					<>
						<AddCampaign open={openAddNew} onClose={() => this.setState({ openAddNew: false })} />

						{/* MAX-3878 — category management dialogs + menus */}
						<AddCategoryDialog
							open={openAddCategory}
							onClose={() => this.setState({ openAddCategory: false })}
						/>
						<RenameCategoryDialog
							group={renameGroup}
							onClose={() => this.setState({ renameGroup: undefined })}
						/>
						<DeleteCategoryDialog
							group={deleteGroup}
							onClose={() => this.setState({ deleteGroup: undefined })}
						/>
						<MoveCampaignDialog
							campaign={campaignToMove}
							sourceName={moveSourceName}
							onClose={() => this.setState({ campaignToMove: undefined })}
						/>

						{/* Category kebab menu — Rename / Delete (red). Delete is disabled with a tooltip
							when this is the last remaining real category. */}
						<Mui.Menu
							anchorEl={categoryMenuAnchor}
							open={!!categoryMenuAnchor}
							onClose={() => this.setState({ categoryMenuAnchor: undefined })}
						>
							<Mui.MenuItem
								onClick={() => this.setState({
									renameGroup: categoryMenuGroup,
									categoryMenuAnchor: undefined,
								})}
							>
								Rename Category
							</Mui.MenuItem>
							{isLastRealCategory ? (
								<Mui.Tooltip title="At least one category is required and cannot be deleted.">
									<span>
										<Mui.MenuItem disabled className={classes.destructiveMenuItem}>
											Delete Category
										</Mui.MenuItem>
									</span>
								</Mui.Tooltip>
							) : (
								<Mui.MenuItem
									className={classes.destructiveMenuItem}
									onClick={() => this.setState({
										deleteGroup: categoryMenuGroup,
										categoryMenuAnchor: undefined,
									})}
								>
									Delete Category
								</Mui.MenuItem>
							)}
						</Mui.Menu>

						{/* Row kebab menu — Move to Category / Duplicate / Delete (per design; Edit is via the campaign title link). */}
						<Mui.Menu
							anchorEl={rowMenuAnchor}
							open={!!rowMenuAnchor}
							onClose={() => this.setState({ rowMenuAnchor: undefined })}
						>
							<Mui.MenuItem
								onClick={() => this.setState({
									campaignToMove: rowMenuCampaign,
									rowMenuAnchor: undefined,
								})}
							>
								Move to Category
							</Mui.MenuItem>
							<Mui.MenuItem
								onClick={() => {
									if (rowMenuCampaign) {
										this.handleDuplicate(rowMenuCampaign);
									}
									this.setState({ rowMenuAnchor: undefined });
								}}
							>
								Duplicate Campaign
							</Mui.MenuItem>
							<Mui.MenuItem
								className={classes.destructiveMenuItem}
								onClick={() => this.setState({
									campaignToDelete: rowMenuCampaign,
									rowMenuAnchor: undefined,
								})}
							>
								Delete Campaign
							</Mui.MenuItem>
						</Mui.Menu>

						{/* Activate confirmation modal — or, for a Market campaign with no Market, the
							MAX-4201 blocking variant instead (never both). */}
						<ActivationDialog
							open={!!campaignToActivate}
							variant={this.state.activationVariant}
							onClose={() => this.setState({ campaignToActivate: undefined })}
							onConfirm={() => this.handleConfirmActivate()}
							onGoToSettings={() => this.handleGoToSettings()}
						/>

						{/* Delete — campaign in use */}
						<Dialog
							open={!!campaignToDelete && isInUse}
							onClose={() => this.setState({ campaignToDelete: undefined })}
							maxWidth="sm"
							fullWidth
						>
							{/* MAX-3842: copy resolved to Figma -- supersedes the interim "delete-both" copy, which
								promised automation deletion when the real (and now correct) behavior is
								preserve-and-deactivate (see handleDeleteConfirm / the ihf-root cascade). */}
							<Mui.DialogTitle>Delete this campaign?</Mui.DialogTitle>
							<Mui.DialogContent>
								<Mui.Typography>
									Deleting {campaignToDelete?.name} will unsubscribe all leads and deactivate any associated automations. This cannot be undone.
								</Mui.Typography>
							</Mui.DialogContent>
							<Mui.DialogActions style={{ padding: '16px 24px' }}>
								<Mui.Button variant="outlined" color="primary" onClick={() => this.setState({ campaignToDelete: undefined })}>
									Cancel
								</Mui.Button>
								<Mui.Button variant="contained" color="primary" onClick={() => campaignToDelete && this.handleDeleteConfirm(campaignToDelete)}>
									Delete Campaign
								</Mui.Button>
							</Mui.DialogActions>
						</Dialog>

						{/* Delete — campaign not in use */}
						<Dialog
							open={!!campaignToDelete && !isInUse}
							onClose={() => this.setState({ campaignToDelete: undefined })}
							maxWidth="sm"
							fullWidth
						>
							<Mui.DialogTitle>Delete campaign?</Mui.DialogTitle>
							<Mui.DialogContent>
								<Mui.Typography>
									This campaign will be permanently deleted. This cannot be undone.
								</Mui.Typography>
							</Mui.DialogContent>
							<Mui.DialogActions style={{ padding: '16px 24px' }}>
								<Mui.Button variant="outlined" color="primary" onClick={() => this.setState({ campaignToDelete: undefined })}>
									Cancel
								</Mui.Button>
								<Mui.Button variant="contained" color="primary" onClick={() => campaignToDelete && this.handleDeleteConfirm(campaignToDelete)}>
									Delete Campaign
								</Mui.Button>
							</Mui.DialogActions>
						</Dialog>

						<Mui.Grid container direction="column" spacing={2}>

							{/* Description */}
							<Mui.Grid item>
								<Mui.Typography variant="body2" color="textSecondary">
									Create and manage campaigns to nurture your leads. To automatically enroll leads, set up{' '}
									<RouterLinkWrapper className={classes.link} to={paths.campaignsRouting}>
										Automations
									</RouterLinkWrapper>.
								</Mui.Typography>
							</Mui.Grid>

							{/* Control row: total count (left) + Add Category / Add Campaign (right) */}
							<Mui.Grid item>
								<div className={classes.controlRow}>
									<Mui.Typography variant="body1" className={classes.totalCount}>
										{grouped.total} Campaigns Total
									</Mui.Typography>
									<div className={classes.controlActions}>
										<Mui.Button
											variant="outlined"
											color="primary"
											startIcon={<Icons.Plus size={16} />}
											onClick={() => this.setState({ openAddCategory: true })}
										>
											Add Category
										</Mui.Button>
										<Mui.Button
											variant="contained"
											color="primary"
											startIcon={<Icons.Plus size={16} />}
											onClick={() => this.setState({ openAddNew: true })}
										>
											Add Campaign
										</Mui.Button>
									</div>
								</div>
							</Mui.Grid>

							{/* One accordion per category (alphabetical), Uncategorized pinned last. */}
							<Mui.Grid item>
								{grouped.groups.map((group) => this.renderCategoryAccordion(group))}
							</Mui.Grid>

						</Mui.Grid>

						{/* MAX-3871 — confirmation toast for the ID column's copy affordance. */}
						<Mui.Snackbar
							open={this.state.showCopiedToast}
							autoHideDuration={3000}
							onClose={() => this.setState({ showCopiedToast: false })}
							anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
						>
							<MuiAlert severity="success" onClose={() => this.setState({ showCopiedToast: false })}>
								<Mui.Typography>Copied</Mui.Typography>
							</MuiAlert>
						</Mui.Snackbar>
					</>
				)}
			</DashboardLayout>
		);
	}
}

export const CampaignsPage = withStyles(connect(mapStateToProps, mapDispatchToProps)(Component), styles);
